import {
  EnumProperty,
  ExternalServiceId,
  ExternalServiceIdProperty,
  IntegerProperty,
  NameProperty,
  NestedProperty,
  Uuid,
  UuidProperty,
} from '@edgebox/data-definition-kit';
import { ContentPriority } from '@edgebox/sync-core-data-definitions';
import type { RemoteEntityPropertyDraft } from '@edgebox/sync-core-rest-client/dist/services/api/models';
import { plainToInstance, Transform, TransformationType, TransformFnParams } from 'class-transformer';
import { validateSync } from 'class-validator';
import { translateEnumSafe } from '../../../enum-labels';
import { transformInteger } from '../ParamsComponent';

/**
 * The number each priority the definitions package declares is prefixed with, mapped back to the
 * member it came from.
 *
 * The site sends the number, never a word, and the word shown to a person is the package's own. The
 * map is derived from the enum, so a priority the package adds, drops or re-words arrives here with
 * it and no list in this repository has to be kept in step. A member whose value carries no number is
 * left out rather than mapped to `NaN`.
 */
export const CONTENT_PRIORITY_BY_NUMBER: ReadonlyMap<number, ContentPriority> = new Map(
  Object.values(ContentPriority)
    .map((member): [number, ContentPriority] => [Number.parseInt(member, 10), member])
    .filter(([value]) => Number.isInteger(value))
);

/**
 * What the box holds a priority by, when the site sent a value this contract does not carry.
 *
 * The sibling transforms of this frame answer a value they cannot read with nothing, because for a
 * count that is what it means: the site holds no figure. A priority is the other case — the contract
 * names four numbers, so a fifth value is a wrong payload rather than a missing figure, and nothing is
 * what the property may not be held by: the kit reads an empty value as a figure left out and asks its
 * decorator nothing about it. What stands for a refusal therefore has to be a value the decorator is
 * given and rejects: a text, which is what the kit holds this property as, that is neither a member of
 * the enum nor empty. A member's own value would be accepted and would put a priority on the screen
 * the site never sent, and the empty string is one of the three the kit reads as a figure left out.
 * This text is neither, and a site sending this very text reaches the decorator with it like any other
 * value the contract does not carry, and is refused the same way.
 *
 * The property's own type names the four members and this is none of them. It is what the box holds a
 * priority by only for as long as it takes the decorator to refuse it: a payload carrying one renders
 * the alert instead of the box, so nothing that reads a priority as a member is ever handed this.
 */
export const REFUSED_CONTENT_PRIORITY = 'not-a-content-priority';

/**
 * The priority the site sent, read back to the member the definitions package declares.
 *
 * The box's contract with the site is the four numbers the enum declares, and a priority is read from
 * one form of them only: the whole value, its surrounding space off, written as that number in decimal.
 * A site's `300` arriving as `"300"` or as `" 300 "` is the three hundred it meant. The number is the
 * prefix of the member's own value, so the member is looked up in the enum and no list of numbers is
 * kept in step here; the record the service answers with carries that number itself and is read by the
 * same rule, so the two sides of the box cannot read a priority differently.
 *
 * Every other value a site sends — a number no member is prefixed with, a spelling that is not the
 * number's own (`"0400"`, `"+400"`, `"400.0"`, `"4e2"`, `"0x190"`, `"400px"`), a member's name, a word,
 * blank text, a boolean, a list, an object — is no priority this contract carries, and it is answered
 * with {@link REFUSED_CONTENT_PRIORITY}: the property's own decorator refuses that and the alert names
 * the property. Only a site holding no priority at all leaves the figure absent, and it says so the two
 * ways a site's storage can: the key left out, or the `null` the row holds.
 *
 * Written back out, a priority the box holds becomes the number the site sent it as, the way every
 * count of this payload writes its own number back out: what leaves this class on the options message
 * is the form a site's markup carries, never a member's name. A value the box refused leaves it as the
 * text that stands for the refusal, so what comes back in is refused again rather than read as a
 * priority the site never sent. A box holding no priority leaves no value on its key, where a count
 * leaves a blank in its place: the key itself is written either way, because the kit exposes every
 * property this class declares, and blank text is a wrong payload for this property where it is a
 * figure left out for a count. A key carrying nothing comes back in as nothing sent, which is where a
 * count's own absence arrives through its blank. Every form this class writes therefore reads back in
 * as the form it was written from, the absent one included.
 */
function transformContentPriority(params: TransformFnParams): unknown {
  if (params.type === TransformationType.CLASS_TO_PLAIN) {
    if (params.value === undefined || params.value === null) {
      return undefined;
    }

    const sent = Array.from(CONTENT_PRIORITY_BY_NUMBER).find(([, member]) => member === params.value);

    return transformInteger({ ...params, value: sent ? sent[0] : params.value });
  } else if (params.type === TransformationType.PLAIN_TO_CLASS) {
    if (params.value === undefined || params.value === null) {
      return undefined;
    }

    const text = typeof params.value === 'string' ? params.value.trim() : undefined;
    const named = Array.from(CONTENT_PRIORITY_BY_NUMBER).find(([number]) => number === params.value || `${number}` === text);

    return named ? named[1] : REFUSED_CONTENT_PRIORITY;
  }

  return params.value;
}

/**
 * How long a tag name may be: the length the term itself is held to.
 *
 * A tag is a taxonomy term, and the store holds a term's name to 200 characters — the bound
 * `SyncCoreSharedTaxonomyTermBase` declares on `name`. A longer name belongs to no term this product
 * can hold, and the two sides of the box answer it the way they answer every bound: a site sending one
 * has sent a wrong payload, so the alert names the tag list, where a record carrying one has that one
 * entry dropped and the page keeps its other tags. The key beside the name is an external service id
 * and carries that id's own length, which is a different thing measured by a different rule: the name
 * is not held to the key's bound because the two sit next to each other on the same entry.
 */
export const MAX_TAG_NAME_LENGTH = 200;

/**
 * How many tags the box shows.
 *
 * A page that has been classified carries a handful — its topics, its audiences, an initiative or two —
 * and twenty is already past what a person reads off a summary box. The number matters because the host
 * page sizes the frame to this document: without a cap, a payload carrying thousands of tags would make
 * the frame grow until it pushed the rest of the site's page off the screen. What the cap leaves out is
 * counted under the chips rather than dropped silently.
 */
export const MAX_TAGS_SHOWN = 20;

/**
 * How many tags the box shows before a person asks for the rest.
 *
 * The box sits in a narrow column beside the site's editor, where a tag's chip takes a line of its own
 * as often as not, so the first few are what a person reads at a glance and the rest wait behind one
 * control that says how many there are. Asked for, the box shows them up to `MAX_TAGS_SHOWN`.
 */
export const COLLAPSED_TAGS_SHOWN = 3;

/**
 * How far ahead of now a stamp may be: room for a clock that runs fast, not for another unit.
 *
 * A sender passing milliseconds lands tens of thousands of years out, so this keeps the wrong unit off
 * the screen instead of drawing a date nobody can read. The limit is measured against the moment the
 * stamp is read, never against a date written down here.
 */
export const MAX_SECONDS_AHEAD_OF_NOW = 365 * 24 * 60 * 60;

/** One tag of the page: the key the syndicated record carries, and the name the sending site resolved. */
export class PageFigureTag {
  @ExternalServiceIdProperty(true)
  key!: ExternalServiceId;

  @NameProperty(true, MAX_TAG_NAME_LENGTH)
  name!: string;
}

/**
 * One page's figures, exactly as the embedding site passes them on the `options` message.
 *
 * The identity is required — it names the page these figures belong to. The page id is the uuid of the
 * entity the site holds for the page, and nothing else: Sync Core keys a page's figures record on that
 * uuid (`remoteUuid` on the content item's entity reference, declared as a uuid in
 * `@edgebox/sync-core-data-definitions`), so no record can exist under an id of another shape and a box
 * accepting one would read live forever and find nothing. A site whose page id is its own — `post:1842`,
 * a node id — is told so here instead.
 *
 * Every figure is optional, and a count the site left out — absent, null or empty — is not an error:
 * the box shows the figures it did receive. A count the site sent as a number is held to its range,
 * because a number outside it would be a wrong figure rather than a missing one; a value spelling no
 * number at all reaches a count as nothing sent, and the tag list, which reads no number, is held to
 * its type. The priority reads none of that: it carries one of the four numbers the enum declares, and
 * the one other thing a payload may say about it is that the site holds none, by leaving the key out or
 * by sending the `null` its storage holds. Every value besides those is an error whose alert names the
 * property — see {@link transformContentPriority}.
 *
 * A number reaches this class as the text the site's markup carries it in, so every count reads
 * through the same transform the rest of this frame's options do: `openIssueCount: "3"` is the three
 * issues the site meant, not a payload this box refuses in front of its visitors.
 *
 * A key this class does not declare is ignored rather than refused, so a site whose payload carries
 * more than the box shows keeps working without a change on its side. `embedSize` is one such key: it
 * belongs to the frame, is set by the library that renders the frame rather than by the calling
 * module, and nothing in this box reads it.
 */
export class PageFiguresParams {
  @ExternalServiceIdProperty(true)
  entityType!: ExternalServiceId;

  // The kit's own uuid property, which is the very rule the definitions package puts on the record's
  // `remoteUuid`: the same decorator, taking any version, so this box takes exactly the page ids the
  // store that holds the record can hold and nothing is restated here. The store's rule is what decides
  // this one, and it is the only thing that does. The route in front of that store matches any 8-4-4-4-12
  // hex string, and the library a site sends this payload with asks for the page's id in whatever form
  // the site has it; both are wider than the store, and a page id wider than the store has no record
  // under it — a box taking one would read live forever and find nothing.
  @UuidProperty(true)
  entityUuid!: Uuid;

  @ExternalServiceIdProperty(true)
  langcode!: ExternalServiceId;

  @IntegerProperty(false, { min: 0, max: 100 })
  @Transform(transformInteger)
  contentHealthPercent?: number;

  @IntegerProperty(false, { min: 0 })
  @Transform(transformInteger)
  openIssueCount?: number;

  // One of the priorities the definitions package declares, named by the number prefixing its member:
  // the site sends the number, and what this box holds is the member, which is what every screen of
  // this app holds a page's priority as. The membership is the enum's own, so a priority the package
  // adds arrives here with it and nothing in this repository has to be kept in step.
  //
  // The options are the box's contract with the site, so a value the contract does not carry is
  // refused here and its alert names the property: a number no member is prefixed with, and equally a
  // spelling that is not one of those numbers written out in decimal. A site holding no priority sends
  // none, by leaving the key out or by sending null. The record Sync Core answers with is the product's
  // own, and a number it carries that names no priority is dropped instead of alerting anyone — see
  // {@link withinOptionBounds}.
  @EnumProperty(false, ContentPriority, 'ContentPriority')
  @Transform(transformContentPriority)
  contentPriority?: ContentPriority;

  @IntegerProperty(false, { min: 0 })
  @Transform(transformInteger)
  citedInAnswersLast30Days?: number;

  // Whether a whole number is a time this box can show is measured against the moment it is read, so
  // it is not a bound the shape can carry either; it is read where the stamp becomes the date on the
  // screen — see {@link isEpochSeconds} and {@link resolvePageFigures}.
  @IntegerProperty(false)
  @Transform(transformInteger)
  summaryUpdated?: number;

  @NestedProperty(false, () => PageFigureTag, { each: true })
  tags?: PageFigureTag[];
}

/**
 * The number a figure carries, or nothing.
 *
 * A site whose storage holds no value for a figure sends `null` or leaves the key out, and both mean
 * the same thing here: there is no number to show. `0` is a number and survives.
 */
export function figureNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * Whether a stamp is a time this box can show: whole seconds since the epoch, not before it, and not
 * so far ahead that it can only be another unit.
 *
 * "Now" is read when the stamp is, so this never goes stale.
 */
export function isEpochSeconds(value: unknown): boolean {
  const seconds = figureNumber(value);
  if (seconds === undefined || !Number.isInteger(seconds) || seconds < 0) {
    return false;
  }
  return seconds <= Date.now() / 1000 + MAX_SECONDS_AHEAD_OF_NOW;
}

/** The text a figure carries once trimmed, or nothing when the site sent none. */
export function figureText(value: unknown): string | undefined {
  const text = typeof value === 'string' ? value.trim() : '';
  return text.length ? text : undefined;
}

/**
 * The word the definitions package gives the priority a page carries.
 *
 * A member the package holds no word for comes back with nothing to show. The box leaves the region
 * off the screen for it, the same as for a page whose site sent no priority at all — so the empty
 * fallback here rather than the dash the screens inside the app draw, where a row keeps its shape and
 * says the value is missing.
 */
export function contentPriorityLabel(priority: ContentPriority | undefined): string | undefined {
  return figureText(translateEnumSafe('ContentPriority', priority, ''));
}

/**
 * The tags to show, one per key.
 *
 * The key identifies the tag on the record, so two entries carrying it are the same tag and the first
 * name wins. The order the site sent is kept.
 *
 * What comes back carries the key and the name as text, so a chip drawn from one and a chip compared
 * against another are the same string: a name a sender padded would otherwise be drawn with its
 * padding, and would tell the box that the figures had changed when nobody would see a change.
 */
export function uniqueTags(tags: PageFigureTag[] | undefined): PageFigureTag[] {
  const byKey = new Map<string, PageFigureTag>();
  for (const tag of tags || []) {
    const key = figureText(tag?.key);
    const name = figureText(tag?.name);
    if (key !== undefined && name !== undefined && !byKey.has(key)) {
      byKey.set(key, plainToInstance(PageFigureTag, { key, name }));
    }
  }
  return Array.from(byKey.values());
}

/** Everything the box shows, once each value the site sent has been resolved to what it renders as. */
export interface IResolvedPageFigures {
  contentHealthPercent?: number;
  importance?: string;
  openIssueCount?: number;
  citedInAnswersLast30Days?: number;
  /** The tags the box shows, at most `MAX_TAGS_SHOWN` of them. */
  tags: PageFigureTag[];
  /** How many the cap left out, which the box says rather than hides. */
  hiddenTagCount: number;
  summaryUpdated?: number;
}

/**
 * Resolve the options into what the box renders.
 *
 * Every region reads its value from here and nowhere else, so whether a page counts as having figures
 * and what actually appears on the screen can never answer differently.
 */
export function resolvePageFigures(figures: PageFiguresParams): IResolvedPageFigures {
  const tags = uniqueTags(figures.tags);

  return {
    contentHealthPercent: figureNumber(figures.contentHealthPercent),
    importance: contentPriorityLabel(figures.contentPriority),
    openIssueCount: figureNumber(figures.openIssueCount),
    citedInAnswersLast30Days: figureNumber(figures.citedInAnswersLast30Days),
    tags: tags.slice(0, MAX_TAGS_SHOWN),
    hiddenTagCount: Math.max(0, tags.length - MAX_TAGS_SHOWN),
    // A whole number that is not a time this box can show is a stamp in another unit, and drawing it
    // would put a date tens of thousands of years out on the screen — see {@link isEpochSeconds}.
    summaryUpdated: isEpochSeconds(figures.summaryUpdated) ? figureNumber(figures.summaryUpdated) : undefined,
  };
}

/**
 * The name the page-figures record stores each figure the box shows under.
 *
 * The record is a flat property list carrying the names the site received, so this map is the one
 * place in the box that knows them. A property the box does not show — the record's own origin key,
 * its content recommendations, the page identity it repeats — is named nowhere here and is therefore
 * ignored.
 */
export const PAGE_FIGURE_PROPERTY_NAMES = {
  contentHealthPercent: 'content_health_percent_0_to_100',
  openIssueCount: 'open_issue_count',
  contentPriority: 'content_priority',
  citedInAnswersLast30Days: 'cited_in_answers_last_30_days',
  summaryUpdated: 'summary_updated',
  tags: 'terms',
} as const;

/** One reference on the record: the referenced entity's own key, and the name resolved for it. */
export interface IPageFigureReference {
  remoteUniqueId?: ExternalServiceId;
  name?: string;
}

/**
 * The record's references as the box's tags.
 *
 * A reference carries the term's key and the name the sending side resolved for it, which is the same
 * pair the embedding site passes in its options. An entry missing either half is kept as it is and
 * left out by `uniqueTags`, so what a tag needs is decided in one place rather than two.
 */
export function referencesAsTags(value: unknown): PageFigureTag[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value.map((entry) => {
    const { remoteUniqueId, name } = (entry ?? {}) as IPageFigureReference;
    // Built the way an entry the site passed is built, so the class's own transforms run over a
    // reference too: assigning the record's values onto an instance skips them, and a key the record
    // carries padded would then be held and compared against a key the same class had trimmed.
    return plainToInstance(PageFigureTag, { key: remoteUniqueId, name });
  });
}

/**
 * The figures held to the bounds an options payload is held to, with every value failing one dropped.
 *
 * The record comes from the service rather than from a person, so a value outside a bound is no reason
 * to put an alert in front of a site's visitors — but it is no reason to draw it either: a health
 * percentage of 150 is a wrong figure, and this box shows a figure or nothing. Dropping it leaves its
 * region off the screen, which is exactly what the box does for a figure the record does not carry at
 * all, so the two sides of the box answer a wrong figure the same way.
 *
 * A priority no member is prefixed with is dropped here, because the shape carries that rule for the
 * options and the record is held to the same shape. What the shape cannot say — which whole numbers are
 * times, measured against the moment the stamp is read — is read where the figure becomes what the
 * screen shows, and covers both sides there.
 *
 * A tag is dropped on its own, so one entry the record cannot be shown for does not take the page's
 * other tags with it. The page identity is never dropped: it is the page the site named, and it
 * already passed this same validation as the site's options.
 */
export function withinOptionBounds(figures: PageFiguresParams): PageFiguresParams {
  const droppable = new Set<string>(Object.keys(PAGE_FIGURE_PROPERTY_NAMES));

  if (figures.tags) {
    figures.tags = figures.tags.filter((tag) => validateSync(tag).length === 0);
  }

  for (const error of validateSync(figures)) {
    if (droppable.has(error.property)) {
      delete (figures as unknown as Record<string, unknown>)[error.property];
    }
  }

  return figures;
}

/**
 * The page's figures as Sync Core holds them, in the shape the embedding site's options arrive in.
 *
 * What this returns goes through `resolvePageFigures` exactly as an options payload does, so a figure
 * read live can never appear differently from the same figure passed by the site. A figure the record
 * does not carry is absent here too, which leaves its region off the screen.
 *
 * The page identity stays the one the site named: it is what the record was asked for, and it is what
 * the box carries as data attributes for the site owner reading them.
 *
 * What comes back is held to the same bounds an options payload is held to, and a value failing one is
 * dropped rather than drawn — see {@link withinOptionBounds}.
 */
export function pageFiguresFromRecord({
  identity,
  properties,
}: {
  identity: PageFiguresParams;
  properties: readonly RemoteEntityPropertyDraft[];
}): PageFiguresParams {
  const byName = new Map(properties.map((property) => [property.name, property.value]));

  // Built the way the site's own options are, so the class's transforms run over the record too and
  // the two sides of the box cannot read the same value by different rules.
  const figures = plainToInstance(PageFiguresParams, {
    entityType: identity.entityType,
    entityUuid: identity.entityUuid,
    langcode: identity.langcode,
    contentHealthPercent: figureNumber(byName.get(PAGE_FIGURE_PROPERTY_NAMES.contentHealthPercent)),
    openIssueCount: figureNumber(byName.get(PAGE_FIGURE_PROPERTY_NAMES.openIssueCount)),
    contentPriority: figureNumber(byName.get(PAGE_FIGURE_PROPERTY_NAMES.contentPriority)),
    citedInAnswersLast30Days: figureNumber(byName.get(PAGE_FIGURE_PROPERTY_NAMES.citedInAnswersLast30Days)),
    summaryUpdated: figureNumber(byName.get(PAGE_FIGURE_PROPERTY_NAMES.summaryUpdated)),
    tags: referencesAsTags(byName.get(PAGE_FIGURE_PROPERTY_NAMES.tags)),
  });

  return withinOptionBounds(figures);
}

/**
 * Whether two resolutions put the same thing in front of a person.
 *
 * Every region the box draws is compared, and only what it draws: the chips by their names in the order
 * they are shown, never by the keys behind them. A key decides which entries collapse into one chip and
 * nothing else — two answers whose chips read the same, in the same order, have changed nothing a
 * person can see, and this is what decides whether there is anything to announce.
 */
export function sameResolvedFigures(one: IResolvedPageFigures, other: IResolvedPageFigures): boolean {
  return (
    one.contentHealthPercent === other.contentHealthPercent &&
    one.importance === other.importance &&
    one.openIssueCount === other.openIssueCount &&
    one.citedInAnswersLast30Days === other.citedInAnswersLast30Days &&
    one.summaryUpdated === other.summaryUpdated &&
    one.hiddenTagCount === other.hiddenTagCount &&
    one.tags.length === other.tags.length &&
    one.tags.every((tag, index) => tag.name === other.tags[index].name)
  );
}

/**
 * Whether anything will be shown for this page.
 *
 * `summaryUpdated` is not counted: it says when the figures were computed, so on its own it dates
 * nothing and the page still has nothing to show.
 */
export function hasAnyFigure(resolved: IResolvedPageFigures): boolean {
  return (
    resolved.contentHealthPercent !== undefined ||
    resolved.importance !== undefined ||
    resolved.openIssueCount !== undefined ||
    resolved.citedInAnswersLast30Days !== undefined ||
    resolved.tags.length > 0
  );
}
